import { useCallback, useEffect, useState } from 'react';

export interface AccessibilityPrefs {
  fontSize: 'default' | 'large' | 'xl';
  reducedMotion: boolean;
  highContrast: boolean;
}

const STORAGE_KEY = 'ecovoz-a11y';

const DEFAULT_PREFS: AccessibilityPrefs = {
  fontSize: 'default',
  reducedMotion: false,
  highContrast: false,
};

function applyPrefsToRoot(prefs: AccessibilityPrefs): void {
  const root = document.documentElement;
  const fontSizePx = prefs.fontSize === 'xl' ? '20px' : prefs.fontSize === 'large' ? '18px' : '';
  root.style.fontSize = fontSizePx;
  root.classList.toggle('a11y-high-contrast', prefs.highContrast);
  root.classList.toggle('a11y-reduce-motion', prefs.reducedMotion);
}

function loadPrefs(): AccessibilityPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      return { ...DEFAULT_PREFS, ...(JSON.parse(raw) as Partial<AccessibilityPrefs>) };
    }
  } catch {
    // ignore parse/storage errors
  }
  return DEFAULT_PREFS;
}

export function useAccessibilityPrefs(): {
  prefs: AccessibilityPrefs;
  setPrefs: (updates: Partial<AccessibilityPrefs>) => void;
} {
  const [prefs, setPrefsState] = useState<AccessibilityPrefs>(loadPrefs);

  useEffect(() => {
    applyPrefsToRoot(prefs);
  }, [prefs]);

  const setPrefs = useCallback((updates: Partial<AccessibilityPrefs>): void => {
    setPrefsState((prev) => {
      const next = { ...prev, ...updates };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // storage full or blocked — silently skip persistence
      }
      return next;
    });
  }, []);

  return { prefs, setPrefs };
}
